import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { Button, CircularProgress, TextField, Tooltip } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import StarRoundedIcon from '@mui/icons-material/StarRounded'

import commentsApi, { CommentItem } from '../../client/comments-api'
import { useAuth } from '../../context/AuthContext'
import { hideTmdbReviews } from '../../utils/comments-visibility'
import { parseUtcDate } from '../../utils/parse-utc-date'
import {
  StyledCommentsSection,
  StyledSectionTitle,
  StyledCommentForm,
  StyledCommentFormActions,
  StyledCommentCard,
  StyledCommentHeader,
  StyledCommentAvatar,
  StyledCommentAvatarImage,
  StyledCommentAuthor,
  StyledCommentAuthorLink,
  StyledCommentMeta,
  StyledCommentSourceChip,
  StyledCommentContent,
  StyledCommentActions,
  StyledCommentActionButton,
  StyledCommentsEmpty,
  StyledCommentsLoginHint,
  StyledCommentReplies,
  StyledReplyButton,
  StyledShowMoreButton,
  StyledPrimaryButton,
  StyledCommentEditActions,
  CommentShowMoreButton,
  StyledReplyForm,
} from './details-page-styles'

const CLAMP_THRESHOLD = 600 // chars; longer comments render clamped with a toggle
const INITIAL_VISIBLE_COMMENTS = 3

type CommentsSectionProps = {
  mediaType: 'Movie' | 'Show' | 'Episode'
  tmdbMediaId: number
  // Episode comments only (DB-only list; TMDB has no per-episode reviews)
  tmdbEpisodeId?: number
  seasonNumber?: number
  episodeNumber?: number
  // When set, the composer is locked (e.g. unreleased titles) and this text
  // explains why. The server enforces the same rule.
  lockedReason?: string
}

const formatDate = (iso: string) =>
  parseUtcDate(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

const CommentsSection: FC<CommentsSectionProps> = ({
  mediaType,
  tmdbMediaId,
  tmdbEpisodeId,
  seasonNumber,
  episodeNumber,
  lockedReason,
}) => {
  const { user, isAuthenticated } = useAuth()

  const [comments, setComments] = useState<CommentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [newContent, setNewContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editContent, setEditContent] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [showAll, setShowAll] = useState(false)
  const [replyingToId, setReplyingToId] = useState<number | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [tmdbHasMore, setTmdbHasMore] = useState(false)
  // Read once per mount - changing the setting re-renders via a page revisit.
  const tmdbHidden = hideTmdbReviews()

  // "Hide TMDB reviews" setting: show only Movieception user comments.
  const visibleComments = tmdbHidden
    ? comments.filter((c) => c.source !== 'TMDB')
    : comments
  const [tmdbPage, setTmdbPage] = useState(1)
  const [loadingMore, setLoadingMore] = useState(false)

  const fetchComments = useCallback(async () => {
    try {
      const response =
        mediaType === 'Episode' && tmdbEpisodeId
          ? await commentsApi.getForEpisode(tmdbEpisodeId)
          : await commentsApi.getForMedia(
              mediaType === 'Movie' ? 'movie' : 'show',
              tmdbMediaId
            )
      setComments(response.data.comments)
      setTmdbHasMore(response.data.tmdbHasMore)
      setTmdbPage(1)
    } catch (error) {
      console.error('Error fetching comments:', error)
    } finally {
      setLoading(false)
    }
  }, [mediaType, tmdbMediaId, tmdbEpisodeId])

  const handleLoadMoreReviews = async () => {
    if (mediaType === 'Episode' || loadingMore) return
    setLoadingMore(true)
    try {
      const nextPage = tmdbPage + 1
      const response = await commentsApi.getMoreTmdbReviews(
        mediaType === 'Movie' ? 'movie' : 'show',
        tmdbMediaId,
        nextPage
      )
      setComments((prev) => [...prev, ...response.data.comments])
      setTmdbHasMore(response.data.tmdbHasMore)
      setTmdbPage(nextPage)
    } catch (error) {
      console.error('Error loading more reviews:', error)
    } finally {
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const handleSubmit = async () => {
    const content = newContent.trim()
    if (!content || submitting) return
    setSubmitting(true)
    try {
      await commentsApi.add({
        Comments_MediaType: mediaType,
        Comments_TmdbMediaId: tmdbMediaId,
        ...(mediaType === 'Episode'
          ? {
              Comments_TmdbEpisodeId: tmdbEpisodeId,
              Comments_SeasonNumber: seasonNumber,
              Comments_EpisodeNumber: episodeNumber,
            }
          : {}),
        Comments_Content: content,
      })
      setNewContent('')
      await fetchComments()
    } catch (error) {
      console.error('Error adding comment:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleReply = async (parentCommentId: number) => {
    const content = replyContent.trim()
    if (!content || submitting) return
    setSubmitting(true)
    try {
      // The server inherits media fields from the parent comment
      await commentsApi.add({
        Comments_MediaType: mediaType,
        Comments_TmdbMediaId: tmdbMediaId,
        Comments_ParentCommentId: parentCommentId,
        Comments_Content: content,
      })
      setReplyContent('')
      setReplyingToId(null)
      await fetchComments()
    } catch (error) {
      console.error('Error adding reply:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSaveEdit = async () => {
    const content = editContent.trim()
    if (editingId == null || !content) return
    try {
      await commentsApi.update(editingId, content)
      setEditingId(null)
      setEditContent('')
      await fetchComments()
    } catch (error) {
      console.error('Error updating comment:', error)
    }
  }

  const handleDelete = async (commentId: number) => {
    try {
      await commentsApi.delete(commentId)
      await fetchComments()
    } catch (error) {
      console.error('Error deleting comment:', error)
    }
  }

  const commentKey = (comment: CommentItem) =>
    comment.source === 'TMDB' ? `tmdb-${comment.tmdbReviewId}` : `user-${comment.id}`

  // Header + content + own-comment actions, shared by top-level comments and replies
  const renderCommentBody = (comment: CommentItem): ReactNode => {
    const key = commentKey(comment)
    const isOwn =
      comment.source === 'User' && user != null && comment.userId === user.id
    const isEditing = isOwn && editingId === comment.id
    const isLong = comment.content.length > CLAMP_THRESHOLD
    const isExpanded = !!expanded[key]

    // Movieception commenters link to their public profile; TMDB reviewers
    // have no profile here, so their identity stays plain text.
    const authorIdentity = (
      <>
        <StyledCommentAvatar>
          {comment.authorAvatarUrl ? (
            <StyledCommentAvatarImage
              src={comment.authorAvatarUrl}
              alt={comment.authorName}
            />
          ) : (
            comment.authorName.charAt(0).toUpperCase()
          )}
        </StyledCommentAvatar>
        <StyledCommentAuthor>{comment.authorName}</StyledCommentAuthor>
      </>
    )

    return (
      <>
        <StyledCommentHeader>
          {comment.source === 'User' && comment.userId != null ? (
            <StyledCommentAuthorLink
              to={`/user/${comment.authorUsername ?? comment.userId}`}
            >
              {authorIdentity}
            </StyledCommentAuthorLink>
          ) : (
            authorIdentity
          )}
          {comment.source === 'TMDB' && (
            <Tooltip title='Review from TMDB'>
              <StyledCommentSourceChip>TMDB</StyledCommentSourceChip>
            </Tooltip>
          )}
          {comment.source === 'TMDB' && comment.authorRating ? (
            <StyledCommentMeta>
              <StarRoundedIcon />
              {comment.authorRating}/10
            </StyledCommentMeta>
          ) : null}
          <StyledCommentMeta>{formatDate(comment.createdOnUtc)}</StyledCommentMeta>
          {isOwn && !isEditing && (
            <StyledCommentActions>
              <Tooltip title='Edit'>
                <StyledCommentActionButton
                  size='small'
                  onClick={() => {
                    setEditingId(comment.id ?? null)
                    setEditContent(comment.content)
                  }}
                >
                  <EditOutlinedIcon />
                </StyledCommentActionButton>
              </Tooltip>
              <Tooltip title='Delete'>
                <StyledCommentActionButton
                  size='small'
                  onClick={() => comment.id != null && handleDelete(comment.id)}
                >
                  <DeleteOutlineIcon />
                </StyledCommentActionButton>
              </Tooltip>
            </StyledCommentActions>
          )}
        </StyledCommentHeader>

        {isEditing ? (
          <>
            <TextField
              multiline
              minRows={2}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              size='small'
              fullWidth
            />
            <StyledCommentEditActions>
              <Button
                size='small'
                onClick={() => {
                  setEditingId(null)
                  setEditContent('')
                }}
              >
                Cancel
              </Button>
              <Button size='small' onClick={handleSaveEdit} disabled={!editContent.trim()}>
                Save
              </Button>
            </StyledCommentEditActions>
          </>
        ) : (
          <>
            <StyledCommentContent clamped={isLong && !isExpanded}>
              {comment.content}
            </StyledCommentContent>
            {isLong && (
              <CommentShowMoreButton
                disableRipple
                onClick={() =>
                  setExpanded((prev) => ({ ...prev, [key]: !isExpanded }))
                }
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </CommentShowMoreButton>
            )}
          </>
        )}
      </>
    )
  }

  if (loading) return null

  return (
    <StyledCommentsSection>
      <StyledSectionTitle>Comments</StyledSectionTitle>

      <StyledCommentForm>
        <TextField
          multiline
          minRows={2}
          placeholder='Write a comment...'
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          size='small'
          fullWidth
          disabled={!isAuthenticated || !!lockedReason}
        />
        <StyledCommentFormActions>
          {!isAuthenticated ? (
            <StyledCommentsLoginHint>
              Log in to join the conversation.
            </StyledCommentsLoginHint>
          ) : lockedReason ? (
            <StyledCommentsLoginHint>
              {lockedReason}
            </StyledCommentsLoginHint>
          ) : null}
          <StyledPrimaryButton
            onClick={handleSubmit}
            disabled={
              !isAuthenticated ||
              !!lockedReason ||
              !newContent.trim() ||
              submitting
            }
          >
            Post Comment
          </StyledPrimaryButton>
        </StyledCommentFormActions>
      </StyledCommentForm>

      {visibleComments.length === 0 ? (
        <StyledCommentsEmpty>No comments yet.</StyledCommentsEmpty>
      ) : (
        (showAll
          ? visibleComments
          : visibleComments.slice(0, INITIAL_VISIBLE_COMMENTS)
        ).map(
          (comment) => (
            <StyledCommentCard key={commentKey(comment)}>
              {renderCommentBody(comment)}

              {/* Replies (user comments only - TMDB reviews are read-only) */}
              {comment.replies && comment.replies.length > 0 && (
                <StyledCommentReplies>
                  {comment.replies.map((reply) => (
                    <div key={commentKey(reply)}>{renderCommentBody(reply)}</div>
                  ))}
                </StyledCommentReplies>
              )}

              {isAuthenticated &&
                !lockedReason &&
                comment.source === 'User' &&
                comment.id != null &&
                (replyingToId === comment.id ? (
                  <StyledReplyForm>
                    <TextField
                      multiline
                      minRows={2}
                      placeholder='Write a reply...'
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      size='small'
                      fullWidth
                      autoFocus
                    />
                    <StyledCommentFormActions>
                      <Button
                        size='small'
                        onClick={() => {
                          setReplyingToId(null)
                          setReplyContent('')
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size='small'
                        onClick={() => handleReply(comment.id!)}
                        disabled={!replyContent.trim() || submitting}
                      >
                        Reply
                      </Button>
                    </StyledCommentFormActions>
                  </StyledReplyForm>
                ) : (
                  <StyledReplyButton
                    disableRipple
                    onClick={() => {
                      setReplyingToId(comment.id!)
                      setReplyContent('')
                    }}
                  >
                    Reply
                  </StyledReplyButton>
                ))}
            </StyledCommentCard>
          )
        )
      )}

      {!showAll && visibleComments.length > INITIAL_VISIBLE_COMMENTS && (
        <StyledShowMoreButton disableRipple onClick={() => setShowAll(true)}>
          Show all {visibleComments.length} comments
        </StyledShowMoreButton>
      )}

      {!tmdbHidden &&
        tmdbHasMore &&
        (showAll || visibleComments.length <= INITIAL_VISIBLE_COMMENTS) && (
          <StyledShowMoreButton
            disableRipple
            onClick={handleLoadMoreReviews}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={16} /> : undefined}
          >
            {loadingMore ? 'Loading reviews...' : 'Load more TMDB reviews'}
          </StyledShowMoreButton>
        )}
    </StyledCommentsSection>
  )
}

export default CommentsSection
